import { useContext, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import ProjectEditingStep from '../../../../entity/ProjectEditingStep';
import { CreationSet, Option } from '../../../../generated/graphql';
import {
  calculateExpressPerDay,
  calculateInputtedSubTotalPoint,
  calculateInputtedTotalPoint,
  calculatePoint,
  calculateSelectedOptionTotalPoint,
} from '../../../../utility/calculatePoint';
import { context as StateContext } from '../../../context/state';
import { editingProjectStateAtom } from '../atoms/editingProjectStateAtom';

// TODO: convert to selector because this useMemo hook only uses state
export const useGetOrderTotal = () => {
  const { creationSet } = useRecoilValue(editingProjectStateAtom);
  return useMemo(() => (creationSet ? creationSet.creativeFormats.length * creationSet.amount : 0), [creationSet]);
};

export const useGetInputtedPointTotal = (step?: ProjectEditingStep) => {
  const { pricingMerchandiseOptions } = useContext(StateContext);
  const { creationSet } = useRecoilValue(editingProjectStateAtom);

  return useMemo(
    () =>
      creationSet
        ? step !== 'submitted'
          ? calculateInputtedTotalPoint(
              creationSet,
              (pricingMerchandiseOptions.availablePricingMerchandiseOptions.map((option) =>
                option.toJSON(),
              ) as unknown) as ReadonlyArray<Option>,
              pricingMerchandiseOptions.selectedPricingMerchandiseOptions,
              creationSet.expressDueDays || undefined,
            )
          : 0
        : 0,
    [
      creationSet,
      pricingMerchandiseOptions.availablePricingMerchandiseOptions,
      pricingMerchandiseOptions.selectedPricingMerchandiseOptions,
      step,
    ],
  );
};

export const useGetExpressDuePointTotal = (creationSet?: CreationSet, step?: ProjectEditingStep) => {
  const { creationSet: inputtedCreationSet } = useRecoilValue(editingProjectStateAtom);

  return useMemo(
    () =>
      creationSet
        ? (() => {
            const creationSetInput = inputtedCreationSet?.id == creationSet.id ? inputtedCreationSet : null;
            const expressDueDays = creationSetInput ? creationSetInput.expressDueDays : creationSet.expressDueDays;
            return step !== 'submitted'
              ? creationSet.creativeFormats
                  .map((creativeFormat) => calculateExpressPerDay(creativeFormat, expressDueDays || undefined))
                  .reduce((a, x) => a + x, 0)
              : 0;
          })()
        : 0,
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creationSet],
  );
};

export const useGetPointSubTotals = (step?: ProjectEditingStep) => {
  const { pricingMerchandiseOptions } = useContext(StateContext);
  const { creationSet } = useRecoilValue(editingProjectStateAtom);

  return useMemo(
    () =>
      creationSet
        ? step !== 'submitted'
          ? calculateInputtedSubTotalPoint(
              creationSet,
              (pricingMerchandiseOptions.availablePricingMerchandiseOptions.map((option) =>
                option.toJSON(),
              ) as unknown) as ReadonlyArray<Option>,
              pricingMerchandiseOptions.selectedPricingMerchandiseOptions,
              creationSet.expressDueDays || undefined,
            )
          : 0
        : null,
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creationSet, pricingMerchandiseOptions.selectedPricingMerchandiseOptions],
  );
};

export const useSelectedOptionTotalPoint = (step?: ProjectEditingStep) => {
  const { creationSet } = useRecoilValue(editingProjectStateAtom);
  const { pricingMerchandiseOptions } = useContext(StateContext);

  return useMemo(
    () =>
      creationSet
        ? step === 'submitted'
          ? calculateSelectedOptionTotalPoint(
              creationSet,
              (pricingMerchandiseOptions.availablePricingMerchandiseOptions.map((option) =>
                option.toJSON(),
              ) as unknown) as ReadonlyArray<Option>,
              pricingMerchandiseOptions.selectedPricingMerchandiseOptions,
            )
          : 0
        : 0,
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creationSet, pricingMerchandiseOptions.selectedPricingMerchandiseOptions],
  );
};

export const useGetRequiredPoints = (creationSet?: CreationSet | null, step?: ProjectEditingStep) => {
  const {
    pricingMerchandiseOptions: { selectedPricingMerchandiseOptions },
  } = useContext(StateContext);

  return useMemo(
    () =>
      creationSet
        ? step !== 'submitted'
          ? creationSet.creativeFormats
              .map((creativeFormat) => calculatePoint(creativeFormat))
              .reduce((a, x) => a + x, 0)
          : 0
        : 0,
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creationSet, selectedPricingMerchandiseOptions],
  );
};
